import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Input, Button } from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import CommonModal from "../../Utils/CommonModal";
import OfferView from "./OfferView";

import { getAllOffer } from "../../../slices/RedemptionOffer/GetAllOffer/thunk";
import { getAllOfferSearch } from "../../../slices/RedemptionOffer/GetAllOfferSearch/thunk";
import { deletePartnerOffer } from "../../../slices/RedemptionOffer/DeleteOffer/thunk";

import { hasPermission } from "../../Utils/UtilityFunctions";

const STATUS_STYLES = {
  Active: {
    bg: "#E7F7EF",
    color: "#1F9254",
    dot: "#1F9254",
  },
  Draft: {
    bg: "#FFF6E7",
    color: "#F59E0B",
    dot: "#F59E0B",
  },
  Expired: {
    bg: "#F3F4F6",
    color: "#6B7280",
    dot: "#6B7280",
  },
};

const TABS = ["All", "Active", "Draft", "Expired"];

function StatusBadge({ status }) {
  const formattedStatus = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "-";

  const style = STATUS_STYLES[formattedStatus] || STATUS_STYLES.Expired;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "20px",
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: "13px",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: style.dot,
          display: "inline-block",
        }}
      />

      {formattedStatus}
    </span>
  );
}

const columns = [
  {
    accessorKey: "offerTitle",
    header: "Offer",
  },
  {
    accessorKey: "category",
    header: "Category",
  },
  {
    accessorKey: "pointsRequired",
    header: "Points",
  },
  {
    accessorKey: "discountValue",
    header: "Discount",
    Cell: ({ row }) => {
      const { discountType, discountValue } = row.original;

      if (discountValue === null || discountValue === undefined) {
        return "-";
      }

      if (discountType?.toLowerCase() === "percentage") {
        return `${discountValue}%`;
      }

      return `₹${discountValue}`;
    },
  },
  {
    accessorKey: "validityStart",
    header: "Start Date",
  },
  {
    accessorKey: "validityEnd",
    header: "End Date",
  },
  {
    accessorKey: "status",
    header: "Status",
    Cell: ({ cell }) => <StatusBadge status={cell.getValue()} />,
  },
  // {
  //   accessorKey: "redeemed",
  //   header: "Redeem",
  // },
];

function OffersListings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const partnerName = localStorage.getItem("userName");
  const operatorPartnerId = localStorage.getItem("operatorId");

  const [operatorId, partnerId] = operatorPartnerId
    ? operatorPartnerId.split("/")
    : [null, null];

  const [activeTab, setActiveTab] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [columnFilters, setColumnFilters] = useState([]);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteOfferData, setDeleteOfferData] = useState(null);
  const [messageForPopUp, setMessageForPopUp] = useState("");

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferView, setShowOfferView] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { offerList } = useSelector((state) => state.GetAllOffer || {});

  const { offerSearchList } = useSelector(
    (state) => state.GetAllOfferSearch || {},
  );

  const rawPermissions = useSelector(
    (state) => state.permissions?.permissionsMap || [],
  );

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );

  const offerPermission = hasPermission(permissionsMap, "cms", "offer");

  const isSearching = searchText.trim().length > 0;

  const tableData = useMemo(() => {
    const response = isSearching ? offerSearchList : offerList;

    if (!response?.success) {
      return [];
    }

    if (!Array.isArray(response.data)) {
      return [];
    }

    return response.data;
  }, [isSearching, offerSearchList, offerList]);

  const totalRows = useMemo(() => {
    const response = isSearching ? offerSearchList : offerList;

    if (typeof response?.meta?.total === "number") {
      return response.meta.total;
    }

    if (typeof response?.meta?.totalCount === "number") {
      return response.meta.totalCount;
    }

    if (typeof response?.meta?.count === "number") {
      return response.meta.count;
    }

    return Array.isArray(response?.data) ? response.data.length : 0;
  }, [isSearching, offerSearchList, offerList]);

  useEffect(() => {
    if (!partnerId) {
      return;
    }

    const searchValue = searchText.trim();

    if (searchValue) {
      dispatch(getAllOfferSearch(partnerId, searchValue, setLoading));

      return;
    }

    const status = activeTab === "All" ? "all" : activeTab.toLowerCase();

    dispatch(
      getAllOffer(
        partnerId,
        pagination.pageIndex + 1,
        pagination.pageSize,
        status,
        setLoading,
      ),
    );
  }, [
    dispatch,
    partnerId,
    searchText,
    activeTab,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleSearch = (value) => {
    setSearchText(value);

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handlePaginationChange = (updater) => {
    setPagination((prev) =>
      typeof updater === "function" ? updater(prev) : updater,
    );
  };

  const toggleDeleteModal = () => {
    setDeleteModal((prev) => !prev);
    document.body.style.overflow = "auto";
  };

  const handleDelete = (offer) => {
    setDeleteOfferData(offer);

    setMessageForPopUp(
      `Are you sure you want to delete offer "${offer?.offerTitle}"?`,
    );

    toggleDeleteModal();
  };

  const handleViewOffer = (offer) => {
    setSelectedOffer(offer);
    setShowOfferView(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteOfferData?.id) {
      return;
    }

    dispatch(
      deletePartnerOffer(deleteOfferData.id, setLoading, () => {
        setDeleteModal(false);
        setDeleteOfferData(null);

        const status = activeTab === "All" ? "all" : activeTab.toLowerCase();

        if (searchText.trim()) {
          dispatch(getAllOfferSearch(partnerId, searchText.trim(), setLoading));
        } else {
          dispatch(
            getAllOffer(
              partnerId,
              pagination.pageIndex + 1,
              pagination.pageSize,
              status,
              setLoading,
            ),
          );
        }
      }),
    );
  };

  const handleEditOffer = (offer) => {
    navigate("/offer-create", {
      state: {
        isEdit: true,
        offer,
      },
    });
  };

  const handleOfferUnpublished = () => {
    // Close right-side OfferView
    setShowOfferView(false);
    setSelectedOffer(null);

    // Refresh listing API
    const status = activeTab === "All" ? "all" : activeTab.toLowerCase();

    if (searchText.trim()) {
      dispatch(getAllOfferSearch(partnerId, searchText.trim(), setLoading));
    } else {
      dispatch(
        getAllOffer(
          partnerId,
          pagination.pageIndex + 1,
          pagination.pageSize,
          status,
          setLoading,
        ),
      );
    }
  };

  return (
    <div id="csms">
      <Container fluid>
        <Row>
          <Col xs={12} lg={showOfferView ? 8 : 12}>
            <Col
              xs={12}
              className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center mb-3 gap-2 gap-md-3"
            >
              <div
                className="d-flex align-items-center gap-1 flex-wrap p-1"
                style={{
                  backgroundColor: "#F4F6FA",
                  borderRadius: "8px",
                  overflowX: "auto",
                  flexShrink: 0,
                }}
              >
                {TABS.map((tab) => {
                  const isActive = activeTab === tab;

                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => handleTabChange(tab)}
                      style={{
                        border: isActive
                          ? "1px solid #ffffff"
                          : "1px solid transparent",
                        background: isActive ? "#fff" : "transparent",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontWeight: 600,
                        fontSize: "14px",
                        color: isActive ? "#FD7545" : "#6B7280",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>

              <div
                className="d-flex align-items-center gap-2 flex-nowrap ms-md-auto"
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  maxWidth: showOfferView ? "320px" : "520px",
                }}
              >
                <Input
                  type="text"
                  placeholder="Search offers..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="flex-grow-1"
                  style={{
                    minWidth: 0,
                    height: "40px",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                  }}
                />

                {offerPermission?.has("create") && (
                  <Button
                    type="button"
                    onClick={() => navigate("/offer-create")}
                    style={{
                      height: "40px",
                      borderRadius: "8px",
                      border: "1px solid #FD7545",
                      backgroundColor: "#FD7545",
                      color: "#fff",
                      fontWeight: 600,
                      padding: "0 16px",
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      +
                    </span>

                    <span className="d-none d-sm-inline">Add Offer</span>
                  </Button>
                )}
              </div>
            </Col>

            <Col xs={12}>
              <div
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                <div
                  className="table-responsive"
                  style={{
                    overflowX: "auto",
                  }}
                >
                  <MaterialReactTable
                    columns={columns}
                    data={tableData}
                    manualPagination
                    rowCount={totalRows}
                    enableRowActions
                    enableTopToolbar={false}
                    enableColumnOrdering={false}
                    enableColumnDragging={false}
                    enableDensityToggle={false}
                    enableColumnActions={false}
                    enableSorting={false}
                    enablePagination
                    onPaginationChange={handlePaginationChange}
                    state={{
                      pagination,
                      columnFilters,
                      showColumnFilters,
                    }}
                    onColumnFiltersChange={setColumnFilters}
                    onShowColumnFiltersChange={setShowColumnFilters}
                    muiTableBodyRowProps={({ row }) => ({
                      onClick: () => handleViewOffer(row.original),
                      sx: {
                        cursor: "pointer",
                        backgroundColor:
                          selectedOffer?.id === row.original.id
                            ? "#fff"
                            : "inherit",
                      },
                    })}
                    muiTableHeadCellProps={{
                      sx: {
                        backgroundColor: "#F4F6FA",
                      },
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => {
                      const actions = [];

                      if (offerPermission?.has("update")) {
                        actions.push(
                          <MenuItem
                            key="edit"
                            onClick={() => {
                              closeMenu();
                              handleEditOffer(row.original);
                            }}
                          >
                            <i className="bx bxs-edit me-2" />
                            Edit
                          </MenuItem>,
                        );
                      }

                      if (offerPermission?.has("view")) {
                        actions.push(
                          <MenuItem
                            key="view"
                            onClick={() => {
                              closeMenu();
                              handleViewOffer(row.original);
                            }}
                          >
                            <i className="ri-eye-line me-2" />
                            View
                          </MenuItem>,
                        );
                      }

                      if (offerPermission?.has("delete")) {
                        actions.push(
                          <MenuItem
                            key="delete"
                            onClick={() => {
                              closeMenu();
                              handleDelete(row.original);
                            }}
                          >
                            <i className="ri-delete-bin-5-line me-2" />
                            Delete
                          </MenuItem>,
                        );
                      }

                      return actions;
                    }}
                    displayColumnDefOptions={{
                      "mrt-row-actions": {
                        header: "",
                        size: 20,
                        minSize: 20,
                        maxSize: 20,
                      },
                    }}
                  />
                </div>
              </div>
            </Col>
          </Col>

          {showOfferView && (
            <Col xs={12} lg={4} className="mt-3 mt-lg-0 border rounded-1">
              <OfferView
                offer={selectedOffer}
                onClose={() => {
                  setShowOfferView(false);
                  setSelectedOffer(null);
                }}
                onEdit={handleEditOffer}
                onUnpublished={handleOfferUnpublished}
              />
            </Col>
          )}
        </Row>
      </Container>

      <CommonModal
        modalHeader="Delete Offer"
        open={deleteModal}
        toggle={toggleDeleteModal}
        buttonText="Yes, Delete It"
        closeButtonText="Cancel"
        messageForPopUp={messageForPopUp}
        modalAction={handleConfirmDelete}
      />
    </div>
  );
}

export default OffersListings;
